import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Col,
  Row,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
} from "reactstrap";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";
import withRouter from "../../../../components/Common/withRouter";
import { withTranslation } from "react-i18next";

import GlobalLoader from "../../../../components/Common/GlobalLoader";
import TableContainerWarehouseRx from "../../../../components/Common/TableContainerWarehouseRx";

import {
  getStaffs as onGetStaffs,
  getStaffDetails,
  deleteStaff,
  generateEmployeePayslip,
} from "../../../../slice/thunks";
import "./staff.css";

const StaffManagement = (props) => {
  const dispatch = useDispatch();
  const history = useNavigate();

  // States
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedRow,setSelectedRow] = useState([]);
  const [isPayslipOpen, setIsPayslipOpen] = useState(false);

  // Redux selector
  const selectLayoutState = (state) => state.Settings;
  const OutputsProperties = createSelector(selectLayoutState, (outputs) => ({
    staffs: outputs.staffs,
    staffDetails: outputs.staffDetails,
    error: outputs.error,
  }));

  const selectPayrollState = (state) => state.Payroll;
const PayrollProperties = createSelector(selectPayrollState, (outputs) => ({
  responseJson: outputs.responseJson,
}));
const {responseJson} = useSelector(PayrollProperties);
// console.log("responseJson",responseJson.status);


  const { staffs, staffDetails } = useSelector(OutputsProperties);

  //  Charger les employés côté serveur
  const loadStaffs = useCallback(
    (page = 1) => {
      const filters = {
        pageNumber: page,
        pageSize,
        employeeStatus: selectedStatus,
        term: searchTerm,
      };
      setIsLoading(true);
      dispatch(onGetStaffs(filters)).then(() => setIsLoading(false));
      setCurrentPage(page);
    },
    [dispatch, pageSize, selectedStatus, searchTerm]
  );

  useEffect(() => {
    loadStaffs();
  }, [loadStaffs]);

  // Actions
  const handleOpen = () => {
    history("/payroll/staff-management/create");
  };

  const handleView = (id) => {
    dispatch(getStaffDetails(id)).then(() => setIsViewOpen(true));
  };

  const handleDeleteClick = (staff) => {
    setStaffToDelete(staff);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!staffToDelete) return;
    dispatch(deleteStaff(staffToDelete.employeeId)).then(() => {
      setIsDeleteModalOpen(false);
      setSuccessMessage(props.t("deleted_successfully"));
      loadStaffs(currentPage);
      setTimeout(() => setSuccessMessage(""), 3000);
    });
  };

  // Colonnes
  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "fullName",
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "employeeStatus",
        enableColumnFilter: false,
      },
      {
        header: "Matricule",
        accessorKey: "matricule",
        enableColumnFilter: false,
      },
      {
        header: "Position",
        accessorKey: "positions",
        enableColumnFilter: false,
      },
      {
        header: "Taxable",
        accessorKey: "taxable",
        cell: (info) => (info.getValue() ? "Yes" : "No"),
        enableColumnFilter: false,
      },
      {
        header: "Actions",
        cell: ({ row }) => {
          const staff = row.original;
          return (
            <div style={{ display: "flex", gap: "12px", position: "relative" }}>
              {/* View */}
              <button
                onClick={() => handleView(staff.employeeId)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#0d6efd",
                }}
                title={props.t("view_details")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#0d6efd" viewBox="0 0 24 24">
                  <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                </svg>
              </button>

              {/* Edit */}
              <button
                onClick={() => history(`/payroll/staff-management/update/${staff.employeeId}`)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "green",
                }}
                title={props.t("update")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="green" viewBox="0 0 24 24">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
                </svg>
              </button>

              {/* Delete */}
              <button
                onClick={() => handleDeleteClick(staff)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "red",
                }}
                title={props.t("delete")}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="red" viewBox="0 0 24 24">
                  <path d="M3 6h18v2H3V6zm2 3h14v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9zm5 3v5h2v-5H10zm4 0v5h2v-5h-2zM9 4h6v2H9V4z" />
                </svg>
              </button>

              {/* Dropdown */}
              <UncontrolledDropdown>
                <DropdownToggle className="btn btn-soft-secondary btn-sm" tag="button">
                  <i className="fa-solid fa-ellipsis"></i>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                  <DropdownItem
                    onClick={() =>
                      history(`/payroll/loans/${staff.employeeId}/assign/${encodeURIComponent(staff.fullName)}`)
                    }
                  >
                    {props.t("assign_loan")}
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem
                    onClick={() =>
                      history(`/payroll/advanced-salary/${staff.employeeId}/assign/${encodeURIComponent(staff.fullName)}`)
                    }
                  >
                    {props.t("assign_advanced_salary")}
                  </DropdownItem>
                   <DropdownItem divider />
                  <DropdownItem
                    onClick={() =>
                      history(`/payroll/deductions/${staff.employeeId}/assign`, {
                        state: {
                          employee: staff
                        }
                      })
                    }
                  >
                    {props.t("assign_deductions")}
                  </DropdownItem>
                  {/* <DropdownItem divider />
                  <DropdownItem
                    onClick={() =>
                      history(`/payroll/bonus/${staff.employeeId}/assign`, {
                        state: {
                          employee: staff
                        }
                      })
                    }
                  >
                    {props.t("assign_bonus")}
                  </DropdownItem> */}
                  <DropdownItem divider />
                  <DropdownItem
                    onClick={() =>
                      history(`/payroll/setting/allowances/${staff.employeeId}/settings`, {
                        state: {
                          employee: staff
                        }
                      })
                    }
                  >
                    {props.t("assign_allowance")}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          );
        },
        enableColumnFilter: false,
      },
    ],
    [props.t, history]
  );


  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const months = [
    { label: "January", value: "1" },
    { label: "February", value: "2" },
    { label: "March", value: "3" },
    { label: "April", value: "4" },
    { label: "May", value: "5" },
    { label: "June", value: "6" },
    { label: "July", value: "7" },
    { label: "August", value: "8" },
    { label: "September", value: "9" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
  ];

  const years = Array.from({ length: 10 }, (_, i) => ({
    label: (new Date().getFullYear() - i).toString(),
    value: (new Date().getFullYear() - i).toString(),
  }));

  const navigate = useNavigate();

  const handleGeneratePayslip = () => {
    // console.log("selectedRow", selectedRow);
    dispatch(generateEmployeePayslip({
      month: selectedMonth,
      year: selectedYear,
      data: {
        employeeIds:selectedRow
      }
    }));
  navigate(`/payroll/paymaster?month=${selectedMonth}&year=${selectedYear}`);
  setIsPayslipOpen(false)
  };



  return (
    <>
      <div className="patients-list-content">
        <div className="patients-list px-2 pb-5">
          <div className="container-fluid">
            <div className="heading-parent-sec">
              <div className="heading-child-one-sec pt-4">
                <h6>{props.t("staff_management")}</h6>
              </div>
              <div className="heading-child-two-sec pt-4">
                <div className="create-new-patient-btn my-2 ps-2" onClick={handleOpen}>
                  <div className="create-new-patient-btn-child-one">
                    <img src="../assets/images/new-patient/img-one.png" className="img-fluid" alt="create icon" />
                  </div>
                  <div className="create-new-patient-btn-child-two">
                    <button className="btn">{props.t("create_new_staff")}</button>
                  </div>
                </div>
              </div>
            </div>

            {successMessage && <Alert color="success" className="mt-2">{successMessage}</Alert>}

            <div className="card border-0 row p-2">
              {/* 🔹 Filtre par status */}
              <Row xl={3} sm={3}>
                <div className="mb-2">
                 <strong> <Label htmlFor="status-select" className="form-label">{props.t("filter_by_status")}</Label></strong>
                  <div className="d-flex align-items-center">
                    <div className="me-2 mb-1 col-10">
                      <select
                        id="status-select"
                        className="form-select custom-border"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                      >
                        <option value="">-- Select Status --</option>
                        <option value="Active">Active</option>
                        <option value="Suspended">Suspended</option>
                        <option value="Sacked">Sacked</option>
                        <option value="InLeave">In Leave</option>
                      </select>
                    </div>
                    <button type="button" className="btn btn-primary" onClick={() => loadStaffs(1)}>
                      <i className="fa fa-search p-0"></i>
                    </button>
                  </div>
                </div>
                <div className="mb-2">
                <strong> <Label htmlFor="search-input">{props.t("search_by_name_matricule")}</Label></strong>
                  <div className="d-flex align-items-center">
                    <Input
                      id="search-input"
                      type="text"
                      placeholder={props.t("enter_employee_name_matricule")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="custom-border me-2"
                      style={{ border: "1px solid #ddd", width: "400px" }}
                    />
                    <Button color="primary" onClick={() => loadStaffs(1)}>
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </div>
                <div className="d-flex justify-content-end align-items-center">
                  <Button
                    color="primary"
                    className=" h-50"
                    onClick={() => setIsPayslipOpen(!isPayslipOpen)}
                    disabled={selectedRow.length === 0}
                  >
                    {props.t("generate_payslip")}
                  </Button>
                </div>

                <Modal isOpen={isPayslipOpen} toggle={() => setIsPayslipOpen(!isPayslipOpen)} size="lg" centered>
                  <ModalHeader toggle={() => setIsPayslipOpen(!isPayslipOpen)}>{props.t("generate_payslip")}</ModalHeader>
                  <ModalBody>
                   {/* Select input for months */}
                   <div className="mb-2">
                    <Label htmlFor="month-select">{props.t("month")}</Label>
                    <select
                      id="month-select"
                      className="form-select custom-border"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      <option value="">-- Select Month --</option>
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                   </div>
                   {/* Select input for year */}
                   <div className="mb-2">
                    <Label htmlFor="year-select">{props.t("year")}</Label>
                    <select
                      id="year-select"
                      className="form-select custom-border"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="">-- Select Year --</option>
                      {years.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </select>
                   </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="secondary" onClick={()=>setIsPayslipOpen(!isPayslipOpen)}>
                      {props.t("cancel")}
                    </Button>
                    <Button color="primary" onClick={handleGeneratePayslip}>
                      {props.t("generate_payslip")}
                    </Button>
                  </ModalFooter>
                </Modal>
              </Row>

              {/* Tableau */}
              <div className="dedection-categories">
                          <div className="container-fluid">
                            <div className="row justify-content-between">
                              <div className="card table-card"> 
                                {/* {console.log("currentpage :",currentPage)} */}
              {isLoading ? (
                <GlobalLoader />
              ) : (
                <TableContainerWarehouseRx
                  columns={columns}
                  data={staffs?.items ?? []}
                  enableRowSelection={true}
                  onSelectionChange={(selectedRows) =>
                    setSelectedRow(selectedRows.map((row) => row.employeeId))
                  }
                  getRowId={(row) => row.employeeId}
                  tableSize={staffs?.size ?? pageSize}
                  tablePage={staffs?.page ?? currentPage}
                  tableTotal={staffs?.total_items ?? 0}
                  tableTotalPage={staffs?.total_page ?? 1}
                  onNextPage={(page) => {
                    loadStaffs(page);
                  }}
                />
              )}      </div>
                            </div>
                          </div>
                        </div>
            </div>
          </div>
        </div>

        {/* 🔹 Modal View */}
        <Modal isOpen={isViewOpen} toggle={() => setIsViewOpen(!isViewOpen)} size="lg" centered>
          <ModalHeader toggle={() => setIsViewOpen(false)}>
            {staffDetails ? staffDetails.fullName : props.t("employee_details")}
          </ModalHeader>
          <ModalBody>
            {staffDetails ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "14px" }}>
                <div><strong>{props.t("full_name")}:</strong> {staffDetails.fullName}</div>
                <div><strong>{props.t("matricule")}:</strong> {staffDetails.matricule}</div>
                <div><strong>{props.t("salary")}:</strong> {staffDetails.salary}</div>
                <div><strong>{props.t("taxable")}:</strong> {staffDetails.taxable ? "Yes" : "No"}</div>
                <div><strong>{props.t("status")}:</strong> {staffDetails.employeeStatus}</div>
                <div><strong>{props.t("staff_type")}:</strong> {staffDetails.staffType}</div>
                <div><strong>{props.t("department")}:</strong> {staffDetails.department}</div>
                <div><strong>{props.t("position")}:</strong> {staffDetails.positions}</div>
                <div><strong>{props.t("echelon")}:</strong> {staffDetails.echelon}</div>
                <div><strong>{props.t("category")}:</strong> {staffDetails.category}</div>
                <div><strong>{props.t("cnps_number")}:</strong> {staffDetails.cnpsNumber}</div>
                <div><strong>{props.t("date_of_birth")}:</strong> {staffDetails.dob}</div>
                <div><strong>{props.t("gender")}:</strong> {staffDetails.gender}</div>
                <div><strong>{props.t("email")}:</strong> {staffDetails.email || "-"}</div>
                <div><strong>{props.t("phone")}:</strong> {staffDetails.phone || "-"}</div>
                <div><strong>{props.t("marital_status")}:</strong> {staffDetails.maritalStatus}</div>
              </div>
            ) : <p>{props.t("loading")}...</p>}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setIsViewOpen(false)}>{props.t("close")}</Button>
          </ModalFooter>
        </Modal>

        {/* 🔹 Modal Delete */}
        <Modal isOpen={isDeleteModalOpen} toggle={() => setIsDeleteModalOpen(!isDeleteModalOpen)} centered>
          <ModalHeader toggle={() => setIsDeleteModalOpen(false)}>{props.t("confirm_delete")}</ModalHeader>
          <ModalBody>
            {props.t("are_you_sure_delete")} <strong>{staffToDelete?.fullName}</strong> ?
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={confirmDelete}>{props.t("delete")}</Button>
            <Button color="secondary" onClick={() => setIsDeleteModalOpen(false)}>{props.t("cancel")}</Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};

export default withRouter(withTranslation()(StaffManagement));